import { useEffect } from 'react';
import type { BlockedCardsProps, WaitingItem } from './types';

/** Enter/Space on a link-styled control, so a breadcrumb works by keyboard too. */
function onActivate(fn: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); }
  };
}

/**
 * LEVEL 3 — the board's blocked cards behind a project's status report, filtered
 * to the open blockers the report counts. Faithful to the approved mockup
 * (dev-preview/portfolio/view-cards.html): a breadcrumb back to the report, a
 * one-line note, then one `kc` card per blocked card — its reference, title, a
 * "Waiting" pill, and a banner that says, in words, what it is waiting on.
 *
 * The real blocked CARDS are the waiting items that carry a `.ref`; the
 * project-level "parked" item has none and is not one of them, so it is excluded.
 */

/** The banner, split into its bold lead and the plain detail that follows — built
 *  from the item's own fields so it never says more than the data knows. A
 *  decision names the call in the lead; everything else names who or what it
 *  waits on, with the typed reason carried as the detail. */
function blockerSentence(item: WaitingItem): { lead: string; rest: string } {
  const target = item.target?.trim();
  // The label is free text and often already begins "Waiting on …"; strip that
  // lead so re-prefixing it below can never read "Waiting on Waiting on …".
  const label = item.label?.trim().replace(/^waiting on\s+/i, '');
  const detail = label && label !== 'something' ? label : '';
  // The detail often already contains the target's own name (target "Pat Moore",
  // detail "Pat Moore — data owner"). When it does, say the name once, not twice —
  // otherwise the banner reads "Waiting on Pat Moore Pat Moore — data owner".
  const detailEchoesTarget = !!(target && detail && detail.toLowerCase().includes(target.toLowerCase()));
  const extra = target && detail && detail !== target && !detailEchoesTarget ? detail : '';

  if (item.type === 'decision') {
    const subject = target || detail;
    return {
      lead: subject ? `Waiting on a decision — ${subject}` : 'Waiting on a decision',
      // When both a named party and a separate typed reason are present, the one
      // the lead did not use rides along as the detail.
      rest: extra,
    };
  }

  // When the detail already names who we wait on, lead with it whole.
  if (detailEchoesTarget) return { lead: `Waiting on ${detail}`, rest: '' };
  const who = target || detail || 'something';
  return { lead: `Waiting on ${who}`, rest: extra };
}

export default function BlockedCards({ row, onBack, onOpenBoard, onOpenCard }: BlockedCardsProps) {
  // Only the waiting items that name a real card belong here.
  const cards = row.waiting.filter((w) => w.ref);
  const n = cards.length;

  // Escape backs out to the project's status report — a keyboard way out.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onBack(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onBack]);

  return (
    <div>
      <div className="pf-crumbs">
        <a role="button" tabIndex={0} onClick={onBack} onKeyDown={onActivate(onBack)}>{row.name}</a> ›{' '}
        <span className="here">Blocked cards ({n})</span>
        <a role="button" tabIndex={0} style={{ marginLeft: 'auto' }} onClick={() => onOpenBoard(row.id)} onKeyDown={onActivate(() => onOpenBoard(row.id))}>
          Open the board →
        </a>
      </div>

      {n === 0 ? (
        <p className="pf-cards-note">Nothing is blocked on this project right now.</p>
      ) : (
        <>
          <p className="pf-cards-note">
            The board, filtered to <b>the {n} open {n === 1 ? 'blocker' : 'blockers'}</b> from the
            report.
          </p>

          <div className="kc-legend">
            <span className="kc-key"><span className="sw" />Waiting on a person or thing</span>
            <span className="kc-key"><span className="sw dec" />Waiting on a decision</span>
          </div>

          {cards.map((item, i) => {
            const { lead, rest } = blockerSentence(item);
            return (
              <button
                type="button"
                key={item.ref ?? i}
                className="kc is-drillable"
                onClick={() => item.ref && onOpenCard(row.id, item.ref)}
                title={item.ref ? `Open ${item.ref}` : undefined}
              >
                <div className="kc-top">
                  <span className="kc-ref">{item.ref}</span>
                  <span className="kc-title">{item.title}</span>
                  <span className="kc-status">
                    <span className="chip">Waiting</span>
                  </span>
                </div>
                <div className={item.type === 'decision' ? 'kc-banner dec' : 'kc-banner'}>
                  <b>{lead}</b>
                  {rest ? ` ${rest}` : ''}
                  {item.days != null && (
                    <>
                      {' '}
                      <span className="since">
                        {item.days} {item.days === 1 ? 'day' : 'days'}
                      </span>
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </>
      )}
    </div>
  );
}
